import React, { useState, useEffect, useCallback } from 'react';
import { X, Mic, Square, Settings, MessageCircle, User, Bot, Waves, Volume2, Play, Pause, Brain } from 'lucide-react';
import { openaiAgentsSDKManager } from '../services/openaiAgentsSDK';
import { getAvailableVoices } from '../services/audioApi';

const OpenAISDKOverlay = ({ 
  onClose,
  selectedVoice = 'alloy',
  onVoiceChange,
  existingConversation = [],
  selectedModel = 'gpt-4o-realtime-preview-2024-12-17'
}) => {
  const [status, setStatus] = useState('idle');
  const [conversation, setConversation] = useState([]);
  const [isRecording, setIsRecording] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [availableVoices, setAvailableVoices] = useState([]);
  const [error, setError] = useState(null);
  const [isAISpeaking, setIsAISpeaking] = useState(false);
  const [isConnected, setIsConnected] = useState(false);
  const [connectionDetails, setConnectionDetails] = useState(null);
  const [isHandsFree, setIsHandsFree] = useState(true); // Default to hands-free listening mode
  const [autoStart, setAutoStart] = useState(true); // Auto-start when overlay opens
  const [isPushToTalk, setIsPushToTalk] = useState(false); // Push-to-talk state
  const [audioLevel, setAudioLevel] = useState(0); // For audio visualization
  const [isVisuallyListening, setIsVisuallyListening] = useState(false); // Visual listening state

  // Initialize conversation with existing chat history
  useEffect(() => {
    if (existingConversation && existingConversation.length > 0) {
      const convertedHistory = existingConversation.map(msg => ({
        role: msg.role,
        content: msg.content,
        timestamp: new Date(msg.timestamp || Date.now()).getTime(),
        audio: null,
        isFromHistory: true
      }));
      
      setConversation(convertedHistory);
      openaiAgentsSDKManager.setInitialConversation(convertedHistory);
    }
  }, [existingConversation]);

  // Load available voices and auto-start if enabled
  useEffect(() => {
    const loadVoices = async () => {
      try {
        const { voices } = await getAvailableVoices();
        setAvailableVoices(voices);
      } catch (error) {
        console.error('Failed to load voices:', error);
      }
    };
    loadVoices();

    // Auto-start conversation if enabled
    if (autoStart && !isConnected) {
      handleInitialize();
    }

    return () => {
      // Cleanup only if initialized
      if (isConnected) {
        openaiAgentsSDKManager.cleanup();
      }
    };
  }, [autoStart]);

  // Handle keyboard events for push-to-talk
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (!isConnected || isHandsFree) return;
      
      if (e.code === 'Space' && !e.repeat) {
        e.preventDefault();
        setIsPushToTalk(true);
      }
    };

    const handleKeyUp = (e) => {
      if (!isConnected || isHandsFree) return;
      
      if (e.code === 'Space') {
        e.preventDefault();
        setIsPushToTalk(false);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    window.addEventListener('keyup', handleKeyUp);

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('keyup', handleKeyUp);
    };
  }, [isConnected, isHandsFree]);

  // Handle push-to-talk state changes
  useEffect(() => {
    if (!isConnected) return;
    
    if (!isHandsFree) {
      if (isPushToTalk) {
        // Start recording when push-to-talk is active
        setIsVisuallyListening(true);
        openaiAgentsSDKManager.startRecording();
      } else {
        // Stop recording when push-to-talk is released
        setIsVisuallyListening(false);
        openaiAgentsSDKManager.stopRecording();
      }
    }
  }, [isPushToTalk, isHandsFree, isConnected]);

  // Update voice when it changes
  useEffect(() => {
    openaiAgentsSDKManager.setVoice(selectedVoice);
  }, [selectedVoice]);

  const handleNewMessage = useCallback((message) => {
    setConversation(prev => {
      const newConversation = [...prev];
      const lastIndex = newConversation.length - 1;
      
      if (lastIndex >= 0 && newConversation[lastIndex].role === message.role && newConversation[lastIndex].isRealtime) {
        // Update existing streaming message
        newConversation[lastIndex] = message;
      } else {
        // Add new message
        newConversation.push(message);
      }
      
      return newConversation;
    });
  }, []);

  const handleError = useCallback((error) => {
    setError(error.message || 'An error occurred');
    setStatus('error');
    setIsConnected(false);
  }, []);

  const handleStatusChange = useCallback((newStatus) => {
    setStatus(newStatus);
    setIsRecording(newStatus === 'recording');
    
    // Update visual listening state
    setIsVisuallyListening(newStatus === 'recording');
    
    // Update connection status
    if (newStatus === 'ready') {
      setIsConnected(true);
    } else if (newStatus === 'disconnected') {
      setIsConnected(false);
    }
  }, []);

  const handleAudioStart = useCallback(() => {
    setIsAISpeaking(true);
  }, []);

  const handleAudioEnd = useCallback(() => {
    setIsAISpeaking(false);
  }, []);

  const handleInitialize = async () => {
    if (isConnected) return;
    
    try {
      setStatus('initializing');
      setError(null);
      
      openaiAgentsSDKManager.setModel(selectedModel);
      openaiAgentsSDKManager.setVoice(selectedVoice);
      
      const success = await openaiAgentsSDKManager.initialize(
        handleNewMessage,
        handleError,
        handleStatusChange,
        handleAudioStart,
        handleAudioEnd
      );

      if (success) {
        setConnectionDetails({
          sdkVersion: '@openai/agents',
          transport: 'WebRTC (Browser)',
          model: selectedModel
        });
      } else {
        setError('Failed to initialize OpenAI Agents SDK');
        setStatus('error');
      }
    } catch (error) {
      handleError(error);
    }
  };

  const handleClearConversation = () => {
    openaiAgentsSDKManager.clearConversation();
    setConversation([]);
  };

  const handleSendTestMessage = () => {
    openaiAgentsSDKManager.sendMessage('Hello! Can you hear me?');
  };

  const handleInterrupt = () => {
    openaiAgentsSDKManager.interrupt();
  };

  const getStatusMessage = () => {
    if (isAISpeaking) return 'AI is speaking...';
    if (isRecording || isPushToTalk || isVisuallyListening) return 'Listening to you...';
    if (status === 'generating') return 'AI is thinking...';
    if (status === 'processing') return 'Processing your speech...';
    if (status === 'initializing') return 'Connecting to OpenAI...';
    if (status === 'error') return error || 'Connection error';
    if (isConnected) {
      if (isHandsFree) {
        return 'Ready - speak naturally';
      } else {
        return 'Ready - hold to speak';
      }
    }
    return 'Click to start voice conversation';
  };

  const getStatusColor = () => {
    if (isRecording || isPushToTalk || isVisuallyListening) return 'text-blue-600';
    if (isAISpeaking) return 'text-green-600';
    if (status === 'generating') return 'text-purple-600';
    if (status === 'processing') return 'text-orange-600';
    if (status === 'error') return 'text-red-600';
    if (isConnected) return 'text-green-600';
    return 'text-gray-600';
  };

  const formatTime = (timestamp) => {
    return new Date(timestamp).toLocaleTimeString([], { 
      hour: '2-digit', 
      minute: '2-digit' 
    });
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl h-[600px] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-200">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-gradient-to-r from-purple-500 to-blue-500 rounded-lg">
              <Waves className="w-5 h-5 text-white" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-gray-900">OpenAI Agents SDK</h2>
              <p className="text-sm text-gray-600">
                Official SDK with {selectedModel}
              </p>
            </div>
          </div>
          
          <div className="flex items-center gap-2">
            {/* Connection indicator */}
            <div className={`w-3 h-3 rounded-full ${
              isConnected ? 'bg-green-500' : 'bg-red-500'
            }`} title={isConnected ? 'Connected' : 'Disconnected'} />
            
            <button
              onClick={() => setShowSettings(!showSettings)}
              className="p-2 rounded-lg text-gray-600 hover:text-gray-800 hover:bg-gray-100 transition-colors"
              title="Settings"
            >
              <Settings className="w-4 h-4" />
            </button>
            
            <button
              onClick={onClose}
              className="p-2 rounded-lg text-gray-600 hover:text-gray-800 hover:bg-gray-100 transition-colors"
              title="Close"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>

        {/* Settings Panel */}
        {showSettings && (
          <div className="p-4 bg-gray-50 border-b border-gray-200 space-y-4">
            <div className="flex items-center gap-4">
              <label className="text-sm font-medium text-gray-700 min-w-16">Voice:</label>
              <select
                value={selectedVoice}
                onChange={(e) => onVoiceChange?.(e.target.value)}
                className="flex-1 text-sm border border-gray-300 rounded px-2 py-1 focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                {availableVoices.map((voice) => (
                  <option key={voice.id} value={voice.id}>
                    {voice.name} - {voice.description}
                  </option>
                ))}
              </select>
            </div>
            
            <div className="text-xs text-gray-600">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <strong>SDK Features:</strong>
                  <ul className="mt-1 space-y-1">
                    <li>• Automatic audio handling</li>
                    <li>• Built-in VAD and interruptions</li>
                    <li>• Official OpenAI integration</li>
                    <li>• WebRTC transport (browser)</li>
                  </ul>
                </div>
                <div>
                  <strong>Status:</strong>
                  <ul className="mt-1 space-y-1">
                    <li>• Connection: {isConnected ? '✅ Active' : '❌ Inactive'}</li>
                    <li>• Recording: {isRecording ? '🎤 Active' : '⏸️ Auto-managed'}</li>
                    <li>• AI Speaking: {isAISpeaking ? '🔊 Active' : '🔇 Silent'}</li>
                  </ul>
                </div>
              </div>
              {connectionDetails && (
                <div className="mt-2 text-xs">
                  <strong>Connection:</strong> {connectionDetails.transport} • {connectionDetails.model}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Status Bar with Mode Toggle */}
        <div className="px-4 py-3 bg-gray-50 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className={`w-2 h-2 rounded-full ${
                status === 'ready' ? 'bg-green-500' :
                (status === 'recording' || isRecording || isPushToTalk) ? 'bg-blue-500 animate-pulse' :
                isAISpeaking ? 'bg-green-500 animate-pulse' :
                status === 'generating' ? 'bg-purple-500 animate-pulse' :
                status === 'processing' ? 'bg-orange-500 animate-pulse' :
                status === 'error' ? 'bg-red-500' :
                'bg-yellow-500 animate-pulse'
              }`} />
              <span className={`text-sm font-medium ${getStatusColor()}`}>
                {getStatusMessage()}
              </span>
            </div>
            
            {/* Mode Toggle */}
            {isConnected && (
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setIsHandsFree(!isHandsFree)}
                  className={`px-3 py-1 text-xs rounded-full border transition-all ${
                    isHandsFree 
                      ? 'bg-green-100 text-green-700 border-green-200' 
                      : 'bg-gray-100 text-gray-600 border-gray-200 hover:bg-gray-200'
                  }`}
                  title={isHandsFree ? 'Switch to Push-to-Talk' : 'Switch to Hands-free'}
                >
                  {isHandsFree ? '🎙️ Hands-free' : '👆 Push-to-Talk'}
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Main Conversation Area */}
        <div className="flex-1 flex items-center justify-center">
          <div className="text-center space-y-6">
            
            {/* Visual Status Indicator */}
            <div className="relative">
              <div 
                className={`w-32 h-32 rounded-full border-4 transition-all duration-300 flex items-center justify-center ${
                  !isHandsFree ? 'cursor-pointer select-none' : ''
                } ${
                  (isRecording || isPushToTalk || isVisuallyListening)
                    ? 'border-blue-500 bg-blue-50 animate-pulse shadow-lg shadow-blue-200' 
                    : isAISpeaking
                    ? 'border-green-500 bg-green-50 animate-pulse shadow-lg shadow-green-200'
                    : status === 'generating'
                    ? 'border-purple-500 bg-purple-50 animate-pulse shadow-lg shadow-purple-200'
                    : status === 'processing'
                    ? 'border-orange-500 bg-orange-50 animate-pulse shadow-lg shadow-orange-200'
                    : status === 'ready' && isConnected
                    ? 'border-green-500 bg-green-50 hover:bg-green-100'
                    : status === 'initializing'
                    ? 'border-purple-300 bg-purple-50 animate-pulse'
                    : 'border-gray-300 bg-gray-50'
                }`}
                onClick={() => {
                  if (!isHandsFree && isConnected && !isPushToTalk) {
                    setIsPushToTalk(true);
                  }
                }}
                onMouseDown={(e) => {
                  if (!isHandsFree && isConnected) {
                    e.preventDefault();
                    setIsPushToTalk(true);
                  }
                }}
                onMouseUp={(e) => {
                  if (!isHandsFree && isConnected) {
                    e.preventDefault();
                    setIsPushToTalk(false);
                  }
                }}
                onTouchStart={(e) => {
                  if (!isHandsFree && isConnected) {
                    e.preventDefault();
                    setIsPushToTalk(true);
                  }
                }}
                onTouchEnd={(e) => {
                  if (!isHandsFree && isConnected) {
                    e.preventDefault();
                    setIsPushToTalk(false);
                  }
                }}
                title={!isHandsFree ? 'Hold to talk (or use spacebar)' : 'Voice conversation active'}
              >
                {(isRecording || isPushToTalk || isVisuallyListening) ? (
                  <div className="flex flex-col items-center">
                    <div className="flex items-center">
                      <Mic className="w-8 h-8 text-blue-600" />
                      <div className="flex gap-1 ml-2">
                        {[...Array(5)].map((_, i) => (
                          <div
                            key={i}
                            className="w-1 bg-blue-500 rounded-full"
                            style={{ 
                              height: `${15 + Math.sin((Date.now() / 100) + (i * 0.5)) * 12}px`,
                              animation: `bounce 0.6s ease-in-out infinite alternate`,
                              animationDelay: `${i * 0.08}s`
                            }}
                          />
                        ))}
                      </div>
                    </div>
                    <div className="text-xs text-blue-600 mt-1 font-medium animate-pulse">
                      🎤 Listening
                    </div>
                  </div>
                ) : isAISpeaking ? (
                  <div className="flex flex-col items-center">
                    <div className="flex items-center">
                      <Bot className="w-8 h-8 text-green-600" />
                      <div className="flex gap-1 ml-2">
                        {[...Array(6)].map((_, i) => (
                          <div
                            key={i}
                            className="w-1 bg-green-500 rounded-full"
                            style={{ 
                              height: `${12 + Math.sin((Date.now() / 60) + (i * 0.7)) * 15}px`,
                              animation: `pulse 1s ease-in-out infinite`,
                              animationDelay: `${i * 0.1}s`
                            }}
                          />
                        ))}
                      </div>
                    </div>
                    <div className="text-xs text-green-600 mt-1 font-medium animate-pulse">
                      🔊 AI Speaking
                    </div>
                  </div>
                ) : status === 'generating' ? (
                  <div className="flex flex-col items-center">
                    <div className="flex items-center">
                      <Brain className="w-8 h-8 text-purple-600" />
                      <div className="flex gap-1 ml-2">
                        {[...Array(3)].map((_, i) => (
                          <div
                            key={i}
                            className="w-2 h-2 bg-purple-500 rounded-full animate-bounce"
                            style={{ animationDelay: `${i * 0.15}s` }}
                          />
                        ))}
                      </div>
                    </div>
                    <div className="text-xs text-purple-600 mt-1 font-medium">
                      🧠 Thinking
                    </div>
                  </div>
                ) : status === 'processing' ? (
                  <div className="flex flex-col items-center">
                    <div className="flex items-center">
                      <Waves className="w-8 h-8 text-orange-600" />
                      <div className="flex gap-1 ml-2">
                        {[...Array(3)].map((_, i) => (
                          <div
                            key={i}
                            className="w-1 h-4 bg-orange-500 rounded-full animate-pulse"
                            style={{ animationDelay: `${i * 0.1}s` }}
                          />
                        ))}
                      </div>
                    </div>
                    <div className="text-xs text-orange-600 mt-1 font-medium">
                      ⚡ Processing
                    </div>
                  </div>
                ) : isConnected ? (
                  <div className="flex flex-col items-center">
                    <MessageCircle className="w-8 h-8 text-green-500" />
                    <div className="text-xs text-gray-600 mt-1 text-center">
                      {isHandsFree ? (
                        <div className="font-medium">Ready to listen</div>
                      ) : (
                        <>
                          <div>Hold to talk</div>
                          <div className="text-xs opacity-70">(Space)</div>
                        </>
                      )}
                    </div>
                  </div>
                ) : (
                  <Waves className="w-8 h-8 text-purple-400" />
                )}
              </div>

              {/* Connection rings for active state */}
              {(isRecording || isVisuallyListening || isPushToTalk || isAISpeaking) && (
                <>
                  <div className={`absolute inset-0 rounded-full border-2 animate-ping opacity-75 ${
                    (isRecording || isVisuallyListening || isPushToTalk) ? 'border-blue-300' : 'border-green-300'
                  }`} />
                  <div className={`absolute inset-0 rounded-full border animate-ping opacity-50 ${
                    (isRecording || isVisuallyListening || isPushToTalk) ? 'border-blue-200' : 'border-green-200'
                  }`} style={{ animationDelay: '0.5s' }} />
                  
                  {/* Extra ring for listening state */}
                  {(isRecording || isVisuallyListening || isPushToTalk) && (
                    <div className="absolute inset-0 rounded-full border border-blue-100 animate-ping opacity-30" 
                         style={{ animationDelay: '1s' }} />
                  )}
                </>
              )}
              
              {/* Hands-free listening indicator */}
              {isHandsFree && isConnected && !isAISpeaking && !status.includes('error') && (
                <div className="absolute -bottom-2 left-1/2 transform -translate-x-1/2">
                  <div className={`px-2 py-1 rounded-full text-xs font-medium transition-all ${
                    (isRecording || isVisuallyListening) 
                      ? 'bg-blue-100 text-blue-700 animate-pulse'
                      : 'bg-green-100 text-green-700'
                  }`}>
                    {(isRecording || isVisuallyListening) ? '🎙️ Listening' : '🔊 Ready'}
                  </div>
                </div>
              )}
            </div>

            {/* Status Text */}
            <div>
              <p className={`text-lg font-medium ${getStatusColor()}`}>
                {getStatusMessage()}
              </p>
              {status === 'ready' && isConnected && (
                <p className="text-gray-500 mt-1">
                  {isHandsFree 
                    ? 'Voice conversation ready - start speaking naturally'
                    : 'Push-to-talk mode - hold the circle or spacebar to speak'
                  }
                </p>
              )}
              
              {isPushToTalk && !isHandsFree && (
                <p className="text-blue-600 font-medium mt-2 animate-pulse">
                  🎙️ Listening... Release to send
                </p>
              )}
            </div>

            {/* Conversation Count */}
            <div className="text-sm text-gray-600 space-y-1">
              {conversation.length > 0 && (
                <>
                  <p>{conversation.filter(m => !m.isFromHistory).length} voice exchanges</p>
                  {existingConversation?.length > 0 && (
                    <p className="text-xs">+ {existingConversation.length} from chat history</p>
                  )}
                </>
              )}
            </div>
          </div>
        </div>

        {/* Controls */}
        <div className="p-6 border-t border-gray-200 bg-white">
          <div className="flex items-center justify-center gap-4">
            <button
              onClick={() => {
                console.log('🔄 Reconnecting...');
                setError(null);
                handleInitialize();
              }}
              disabled={isConnected && !error}
              className={`px-6 py-3 rounded-full font-medium transition-all duration-200 ${
                (isConnected && !error)
                  ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                  : 'bg-gradient-to-r from-purple-500 to-blue-500 hover:from-purple-600 hover:to-blue-600 text-white hover:scale-105'
              }`}
              title={isConnected ? 'Already connected' : 'Start voice conversation'}
            >
              {(isConnected && !error) ? 'Connected' : error ? 'Reconnect' : 'Start Voice Chat'}
            </button>

            {isConnected && (
              <>
                <button
                  onClick={() => {
                    console.log('🧪 Sending test message...');
                    handleSendTestMessage();
                  }}
                  className="px-4 py-2 text-sm bg-blue-500 hover:bg-blue-600 text-white rounded-lg transition-colors"
                  title="Send test message to verify connection"
                >
                  🧪 Test Chat
                </button>

                <button
                  onClick={handleInterrupt}
                  disabled={!isAISpeaking}
                  className="px-4 py-2 text-sm bg-orange-500 hover:bg-orange-600 disabled:bg-gray-300 text-white rounded-lg transition-colors"
                  title="Interrupt AI speech"
                >
                  Interrupt
                </button>

                {conversation.length > 0 && (
                  <button
                    onClick={handleClearConversation}
                    className="px-4 py-2 text-sm text-gray-600 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                    title="Clear conversation"
                  >
                    Clear
                  </button>
                )}
              </>
            )}
          </div>

          <div className="text-center mt-3">
            <p className="text-xs text-gray-500">
              {isConnected ? 
                'Powered by OpenAI Agents SDK • WebRTC Transport • Auto VAD' :
                'Official OpenAI SDK for reliable voice conversations'
              }
            </p>
            {error && (
              <div className="text-center mt-3">
                <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                  <p className="text-sm text-red-600 font-medium mb-1">
                    ❌ Connection Error
                  </p>
                  <p className="text-xs text-red-500">
                    {error}
                  </p>
                  <button
                    onClick={() => {
                      setError(null);
                      handleInitialize();
                    }}
                    className="mt-2 px-3 py-1 text-xs bg-red-500 hover:bg-red-600 text-white rounded transition-colors"
                  >
                    Retry Connection
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default OpenAISDKOverlay;